import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Link from 'flarum/common/components/Link';
import StockTable from './StockTable';
import MarketDiscussions from './MarketDiscussions';
import { loadMarkets, stocksOf, marketStatus, naira, pct, tone, updatedText, stockHref, sectorsOf } from '../util';

/**
 * /markets/ngx: the NGX at a glance, the top listed companies (each with its own page, all searchable), the day's
 * biggest movers, sectors (folded away), and what members are saying in Finance & Business. The share converter
 * is on each company's page; commodities and the key figures are on the Markets hub (/markets, MarketsHubPage.js).
 */
export default class MarketsPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.markets = null;
    this.error = null;
    this.moverSpan = 'today';
    this.sector = null;
    app.setTitle('Nigerian stock market today: NGX share prices');
    loadMarkets().then(d => this.markets = d).catch(() => this.error = 'Could not load market data. Please try again shortly.').then(() => m.redraw());
  }
  view() {
    const stocks = stocksOf(this.markets);
    const status = marketStatus(this.markets);
    return m('.VouchaMarkets', [
    // phones: the page's name in the header bar (Flarum places .App-titleControl there)
    m('h4.App-titleControl.App-titleControl--text', 'NGX'), m(Link, {
      className: 'VouchaMk-back',
      href: app.route('voucha.markets')
    }, '← Markets'), m('header.VouchaMk-head', [m('h1', 'Nigerian stock market today'), m('p.helpText', 'NGX share prices, the biggest movers and every listed company. Prices update every 30 minutes while the market is open.'), this.markets ? m('.VouchaMarkets-updated', [status ? m('span.VouchaMarkets-statusBadge', {
      className: 'is-' + status.toLowerCase().replace(/\s+/g, '-')
    }, 'Market ' + status.toLowerCase()) : null, m('span', updatedText(this.markets.stocks && this.markets.stocks.updated_at))]) : null]), this.error ? m('.VouchaMarkets-error', this.error) : null, !this.markets && !this.error ? m('.VouchaMarkets-loading', 'Loading market data…') : null,
    // the companies first (that's where people look for a company), then movers and sectors
    this.markets && stocks.length ? this.glance(stocks) : null, this.markets && stocks.length ? m(StockTable, {
      stocks,
      sector: this.sector,
      onClearSector: () => this.sector = null
    }) : this.markets ? m('section.VouchaMarkets-section', m('p.VouchaMarkets-empty', 'Stock market data is not available yet.')) : null, this.markets && stocks.length ? [this.movers(stocks), this.sectors(stocks)] : null, m(MarketDiscussions, {
      title: 'Talk about the market',
      empty: 'No discussions yet. Start one in Finance & Business.'
    }), m('p.VouchaMk-source', 'Share prices from the Nigerian Exchange (via NGXPulse). Not investment advice.')]);
  }
  glance(stocks) {
    let cap = 0;
    let up = 0;
    let down = 0;
    stocks.forEach(s => {
      cap += Number(s.market_cap) || 0;
      if (tone(s.change_percent) === 'is-up') up++;else if (tone(s.change_percent) === 'is-down') down++;
    });
    const card = (label, value, cls) => m('.VouchaMarkets-summaryCard', [m('span', label), m('strong', {
      className: cls || ''
    }, value)]);
    return m('section.VouchaMk-glance', [card('Market value', naira(cap, true)), card('Up today', up, 'is-up'), card('Down today', down, 'is-down'), card('Unchanged', stocks.length - up - down)]);
  }
  movers(stocks) {
    const key = this.moverSpan === 'today' ? 'change_percent' : 'pct_change_7d';
    const valid = stocks.filter(s => s[key] !== null && s[key] !== undefined && !isNaN(Number(s[key])));
    const gainers = valid.filter(s => Number(s[key]) > 0).sort((a, b) => b[key] - a[key]).slice(0, 5);
    const losers = valid.filter(s => Number(s[key]) < 0).sort((a, b) => a[key] - b[key]).slice(0, 5);
    const list = (title, items, none) => m('.VouchaMk-moverCol', [m('h3', title), items.length ? m('ol.VouchaMk-movers', items.map(s => m('li', m(Link, {
      href: stockHref(s.symbol)
    }, [m('span.VouchaMk-moverName', [m('strong', s.symbol), m('small', s.name)]), m('span.VouchaMk-moverNum', [m('span', naira(s.current_price)), m('b', {
      className: tone(s[key])
    }, pct(s[key]))])])))) : m('p.VouchaMarkets-note', none)]);
    const tab = (k, label) => m('button', {
      type: 'button',
      className: this.moverSpan === k ? 'is-on' : '',
      'aria-pressed': this.moverSpan === k ? 'true' : 'false',
      onclick: () => this.moverSpan = k
    }, label);
    return m('section.VouchaMarkets-section', [m('.VouchaMk-sectionHead', [m('h2', 'Biggest movers'), m('.VouchaConv-modes', {
      role: 'group',
      'aria-label': 'Period'
    }, [tab('today', 'Today'), tab('7d', '7 days')])]), m('.VouchaMk-moverCols', [list('Gainers', gainers, 'No gainers ' + (key === 'change_percent' ? 'today' : 'this week') + '.'), list('Losers', losers, 'No losers ' + (key === 'change_percent' ? 'today' : 'this week') + '.')])]);
  }
  sectors(stocks) {
    const groups = sectorsOf(stocks);
    if (groups.length < 2) return null;
    return m('details.VouchaMarkets-section.VouchaMk-sectorsBox', {
      open: this.sectorsOpen,
      ontoggle: e => this.sectorsOpen = e.target.open
    }, [m('summary.VouchaMk-sectionHead', [m('h2', 'Sectors'), m('span.VouchaMarkets-note', groups.length + ' sectors · share of market value and change today')]), m('ul.VouchaMk-sectors', groups.map(g => m('li', m('button', {
      type: 'button',
      className: this.sector === g.name ? 'is-on' : '',
      onclick: () => {
        this.sector = this.sector === g.name ? null : g.name;
        setTimeout(() => {
          const el = document.getElementById('all-stocks');
          if (el && this.sector) el.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }, 30);
      }
    }, [m('span.VouchaMk-sectorName', [g.name, m('small', g.count + ' compan' + (g.count === 1 ? 'y' : 'ies'))]), m('span.VouchaMk-sectorBar', {
      'aria-hidden': 'true'
    }, m('i', {
      style: {
        width: Math.max(1.5, g.share * 100).toFixed(1) + '%'
      }
    })), m('span.VouchaMk-sectorShare', (g.share * 100).toFixed(g.share < 0.1 ? 1 : 0) + '%'), m('b', {
      className: tone(g.change)
    }, g.change === null ? '—' : pct(g.change))]))))]);
  }
}
flarum.reg.add('voucha-markets', 'forum/components/MarketsPage', MarketsPage);